// PRD.276 — Elia AI Doctor Consult Integration.
//
// Per-context in-memory threads (FR-8). Keeps a separate conversation per
// general / patient / visit context so histories are never mixed. Pure and
// framework-free — wraps the PRD.275 single-thread reducer, so all existing
// send/retry/reset semantics are inherited unchanged.

import {
  eliaThreadReducer,
  type EliaThreadAction,
  type EliaThreadState,
} from './elia-chat-reducer';
import type { EliaContextSelector, EliaMessage } from './elia-chat-types';

export type EliaThreadsState = {
  threads: Record<string, EliaThreadState>;
};

export type EliaThreadsAction =
  | { type: 'ENSURE'; key: string; initial: EliaMessage[] }
  | { type: 'THREAD'; key: string; initial: EliaMessage[]; action: EliaThreadAction };

export function createInitialThreadsState(): EliaThreadsState {
  return { threads: {} };
}

/** Stable key so each patient/visit/general context owns an isolated thread. */
export function eliaContextKey(selector: EliaContextSelector | undefined): string {
  if (selector?.visitId) return `visit:${selector.visitId}`;
  if (selector?.patientId) return `patient:${selector.patientId}`;
  return 'general';
}

export function eliaThreadsReducer(
  state: EliaThreadsState,
  action: EliaThreadsAction,
): EliaThreadsState {
  switch (action.type) {
    case 'ENSURE': {
      if (state.threads[action.key]) return state;
      return {
        threads: {
          ...state.threads,
          [action.key]: { messages: action.initial, status: 'idle', failedQuery: null },
        },
      };
    }
    case 'THREAD': {
      const current: EliaThreadState = state.threads[action.key] ?? {
        messages: action.initial,
        status: 'idle',
        failedQuery: null,
      };
      const next = eliaThreadReducer(current, action.action);
      if (next === current && state.threads[action.key]) return state;
      return { threads: { ...state.threads, [action.key]: next } };
    }
    default:
      return state;
  }
}
